import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, FileText, Cable, Loader2, Database, Mail, Upload as UploadIcon, AlertCircle, ArrowRight, TrendingUp, ShieldAlert } from 'lucide-react';
import { rassegne, connectors } from '../lib/data';
const providerMeta: Record<string, {
  label: string;
  icon: ComponentType<any>;
}> = {
  mimesi: {
    label: 'Mimesi',
    icon: Database
  },
  eco_stampa: {
    label: "L'Eco della Stampa",
    icon: Database
  },
  data_stampa: {
    label: 'Data Stampa',
    icon: Database
  },
  email: {
    label: 'Email',
    icon: Mail
  },
  upload: {
    label: 'Upload manuale',
    icon: UploadIcon
  }
};
function KPI({
  label,
  value,
  icon: Icon,
  accent = false
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  accent?: boolean;
}) {
  return <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-3" data-id="element-941">
      <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal font-heading" data-id="element-942">
        <Icon className={`h-4 w-4 ${accent ? 'text-accent animate-spin-slow' : ''}`} data-id="element-943" />
        {label}
      </div>
      <div className={`${`text-3xl md:text-4xl font-light leading-none ${accent ? 'text-accent' : 'text-foreground'}`}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-944">
        {value}
      </div>
    </div>;
}
function getStatusColor(status: string) {
  if (status === 'connected') return '#4A7C59';
  if (status === 'error') return '#B85450';
  return '#8B8680';
}
function getStatusLabel(status: string) {
  if (status === 'connected') return 'Connesso';
  if (status === 'error') return 'Errore';
  return 'Disconnesso';
}
export function Homepage() {
  const navigate = useNavigate();
  // Calculate aggregates
  const totalRassegne = rassegne.length;
  const totalArticles = rassegne.reduce((acc, r) => acc + r.articleCount, 0);
  const activeConnectors = connectors.filter(c => c.status === 'connected').length;
  const processingRassegne = rassegne.filter(r => r.status === 'processing').length;
  // Campaign summary aggregates
  const weightedScoreSum = rassegne.reduce((acc, r) => acc + r.avgScore * r.articleCount, 0);
  const avgScore = totalArticles > 0 ? weightedScoreSum / totalArticles : 0;
  const totalAVE = rassegne.reduce((acc, r) => acc + r.totalAVE, 0);
  const totalAntisemitismFlags = rassegne.reduce((acc, r) => acc + r.antisemitismFlags, 0);
  const sentiment = rassegne.reduce((acc, r) => ({
    positive: acc.positive + r.sentimentMix.positive,
    neutral: acc.neutral + r.sentimentMix.neutral,
    negative: acc.negative + r.sentimentMix.negative
  }), {
    positive: 0,
    neutral: 0,
    negative: 0
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-945">
      {/* Header */}
      <header data-id="element-946">
        <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-947">
          Panoramica
        </h1>
      </header>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-948">
        <KPI label="Rassegne caricate" value={totalRassegne} icon={Newspaper} data-id="element-949" />
        <KPI label="Articoli estratti" value={totalArticles} icon={FileText} data-id="element-950" />
        <KPI label="Connettori attivi" value={activeConnectors} icon={Cable} data-id="element-951" />
        <KPI label="In elaborazione" value={processingRassegne} icon={Loader2} accent data-id="element-952" />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-953">
        {/* Left Column: Connectors */}
        <div className="space-y-6" data-id="element-954">
          <div className="bg-card border border-border rounded-lg flex flex-col h-full" data-id="element-955">
            <div className="px-6 pt-6 pb-3 border-b border-border" data-id="element-956">
              <h3 className="text-lg font-normal" data-id="element-957">Stato connettori</h3>
            </div>
            <div className="flex-1" data-id="element-958">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_100px_100px] gap-2 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border font-heading" data-id="element-959">
                <span data-id="element-960">Connettore</span>
                <span className="text-center" data-id="element-961">Stato</span>
                <span className="text-right" data-id="element-962">Rassegne</span>
              </div>
              {/* Table rows */}
              <div className="divide-y divide-border" data-id="element-963">
                {connectors.map(connector => {
                const meta = providerMeta[connector.id];
                const Icon = meta?.icon || Cable;
                return <div key={connector.id} className="grid grid-cols-[1fr_100px_100px] gap-2 items-center px-6 py-4" data-id="element-964">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-965">
                        <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-966">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" data-id="element-967" />
                        </div>
                        <div className="min-w-0" data-id="element-968">
                          <div className="text-sm font-normal truncate" data-id="element-969">
                            {connector.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5" data-id="element-970">
                            {connector.lastSync ? new Date(connector.lastSync).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Mai sincronizzato'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-1.5" data-id="element-971">
                        <div className="w-1.5 h-1.5 rounded-full" style={{
                      backgroundColor: getStatusColor(connector.status)
                    }} data-id="element-972" />
                        <span className="text-xs text-muted-foreground" data-id="element-973">
                          {getStatusLabel(connector.status)}
                        </span>
                      </div>
                      <div className="text-sm text-right text-muted-foreground" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-974">
                        {connector.rassegneIngested}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Recent Activity */}
        <div className="space-y-6" data-id="element-975">
          <div className="bg-card border border-border rounded-lg flex flex-col h-full" data-id="element-976">
            <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b border-border" data-id="element-977">
              <h3 className="text-lg font-normal" data-id="element-978">Attività recenti</h3>
              <button onClick={() => navigate('/rassegne')} className="text-xs text-accent hover:underline flex items-center gap-1" data-id="element-979">
                Vedi tutte
                <ArrowRight className="h-3 w-3" data-id="element-980" />
              </button>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-[1fr_80px_60px] gap-2 px-6 py-3 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border font-heading" data-id="element-981">
              <span data-id="element-982">Rassegna</span>
              <span className="text-center" data-id="element-983">Periodo</span>
              <span className="text-right" data-id="element-984">Art.</span>
            </div>

            <div className="divide-y divide-border flex-1" data-id="element-985">
              {rassegne.slice(0, 6).map(r => {
              const meta = providerMeta[r.provider];
              const Icon = meta?.icon || Database;
              return <button key={r.id} onClick={() => navigate(`/rassegne/${r.id}`)} className="w-full text-left grid grid-cols-[1fr_80px_60px] gap-2 items-center px-6 py-3.5 hover:bg-muted/30 transition-colors" data-id="element-986">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-987">
                      <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-988">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" data-id="element-989" />
                      </div>
                      <div className="min-w-0" data-id="element-990">
                        <div className="text-sm font-normal leading-tight truncate" data-id="element-991">
                          {r.name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5" data-id="element-992">
                          <span className="text-xs text-muted-foreground" data-id="element-993">
                            {meta?.label}
                          </span>
                          {r.status === 'processing' && <div className="flex items-center gap-1 text-accent" data-id="element-994">
                              <Loader2 className="h-3 w-3 animate-spin" data-id="element-995" />
                              <span className="text-[11px]" data-id="element-996">Elab.</span>
                            </div>}
                          {r.status === 'error' && <div className="flex items-center gap-1 text-destructive" data-id="element-997">
                              <AlertCircle className="h-3 w-3" data-id="element-998" />
                              <span className="text-[11px]" data-id="element-999">Errore</span>
                            </div>}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-center" data-id="element-1000">
                      {r.dateRange}
                    </div>
                    <div className="text-sm text-right" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-1001">
                      {r.articleCount}
                    </div>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Full-width: Campaign Summary */}
      <div className="bg-card border border-border rounded-lg p-6" data-id="element-1002">
        <h3 className="text-lg font-normal mb-6" data-id="element-1003">Andamento campagna</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-border" data-id="element-1004">
          <div className="flex flex-col gap-2 md:px-6 first:px-0 pt-4 md:pt-0 first:pt-0" data-id="element-1005">
            <div className="text-sm text-muted-foreground font-normal font-heading" data-id="element-1006">
              AVE totale
            </div>
            <div className="text-3xl font-light" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-1007">
              €{(totalAVE / 1000).toFixed(0)}k
            </div>
          </div>

          <div className="flex flex-col gap-2 md:px-6 pt-4 md:pt-0" data-id="element-1008">
            <div className="text-sm text-muted-foreground font-normal font-heading" data-id="element-1009">
              Score medio
            </div>
            <div className="flex items-center gap-3" data-id="element-1010">
              <div className="text-3xl font-light text-accent" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1011">
                {avgScore.toFixed(1)}
              </div>
              <TrendingUp className="h-5 w-5 text-accent" data-id="element-1012" />
            </div>
          </div>

          <div className="flex flex-col gap-2 md:px-6 pt-4 md:pt-0" data-id="element-1013">
            <div className="text-sm text-muted-foreground font-normal font-heading" data-id="element-1014">
              Sentiment (Pos / Neu / Neg)
            </div>
            <div className="flex items-baseline gap-2 leading-none mt-1" data-id="element-1015">
              <span className="text-3xl font-light text-[#4A7C59]" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1016">
                {sentiment.positive}
              </span>
              <span className="text-xl text-muted-foreground font-light" data-id="element-1017">
                /
              </span>
              <span className="text-2xl text-muted-foreground font-light" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1018">
                {sentiment.neutral}
              </span>
              <span className="text-xl text-muted-foreground font-light" data-id="element-1019">
                /
              </span>
              <span className="text-2xl text-[#B85450] font-light" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-1020">
                {sentiment.negative}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}